/**
 * TransferDocumentForm — Phase 116 / 121
 *
 * Form for warehouse transfer documents.
 * Supports two modes based on the document prefix:
 *   - WHTCO (consignment): pick a consignment client, destination warehouse
 *     is auto-resolved from the client's consignment_warehouse_id.
 *   - WHT (standard): pick source and destination warehouses directly,
 *     no client involved (internal warehouse-to-warehouse transfer).
 *
 * Calls process_inventory_document RPC with source_warehouse_id and
 * destination_warehouse_id in payload. The RPC handles 'transfer'
 * inventory_effect: subtracts from source, adds to destination.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDocLineSort, sortDocLines } from '@/hooks/useDocLineSort';
import type { InventoryPrintData } from '@/components/inventory/InventoryDocumentPrintView';
import {
  Plus, Trash2, Loader as Loader2, CircleAlert as AlertCircle,
  Calendar, Users, Warehouse as WarehouseIcon, FileText,
  ChevronDown, Hash, ArrowLeftRight, ReceiptText, Download,
} from 'lucide-react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { SortableTh } from '@/components/ui/SortableTh';
import DocumentPickerModal, { type ImportableLine } from '@/components/modals/DocumentPickerModal';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Client {
  id:                       string;
  name:                     string;
  consignment_warehouse_id: string | null;
}

interface Warehouse {
  id:   string;
  name: string;
}

interface Product {
  id:    string;
  sku:   string;
  name:  string;
  cost:  number | null;
  price: number;
}

interface DocumentType {
  id:                string;
  name:              string;
  next_number:       number;
  prefix:            string;
  requires_receipt:  boolean;
  is_vat_liable:     boolean;
  updates_inventory: boolean;
  has_line_discount: boolean;
}

interface DocLine {
  product_id: string;
  quantity:   string;
  unit_price: string;
  line_total: number;
}

const EMPTY_LINE: DocLine = {
  product_id: '',
  quantity:   '1',
  unit_price: '0',
  line_total: 0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);
const parseNum = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const calcLine = (qty: number, price: number) => qty * price;

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  docType:              DocumentType;
  companyId:            string;
  vatRate:              number;
  showToast:            (msg: string, ok?: boolean) => void;
  onSuccess:            (docTypeName: string, docRef: string, printData: InventoryPrintData) => void;
  onBeforeFirstSubmit?: () => Promise<boolean>;
  initialLines?:        ImportableLine[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TransferDocumentForm({
  docType,
  companyId,
  vatRate,
  showToast,
  onSuccess,
  onBeforeFirstSubmit,
  initialLines,
}: Props) {

  // ── Reference data ────────────────────────────────────────────────────────
  const [clients,    setClients]    = useState<Client[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products,   setProducts]   = useState<Product[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError,   setRefError]   = useState<string | null>(null);

  const isConsignment = docType.prefix === 'WHTCO' || docType.prefix === 'RTVCO';
  const isReturn = docType.prefix === 'RTVCO';

  const fetchRefData = useCallback(async () => {
    if (!companyId) return;
    setRefLoading(true); setRefError(null);
    try {
      const queries: PromiseLike<any>[] = [
        supabase
          .from('inventory_warehouses')
          .select('id, name')
          .eq('company_id', companyId)
          .order('name'),
        supabase
          .from('inventory_products')
          .select('id, sku, name, cost, price')
          .eq('company_id', companyId)
          .order('name'),
      ];
      if (isConsignment) {
        queries.unshift(
          supabase
            .from('inventory_clients')
            .select('id, name, consignment_warehouse_id')
            .eq('company_id', companyId)
            .eq('allow_consignment', true)
            .order('name'),
        );
      }
      const results = await Promise.all(queries);
      for (const r of results) { if (r.error) throw r.error; }
      if (isConsignment) {
        setClients(results[0].data as Client[]);
        setWarehouses(results[1].data as Warehouse[]);
        setProducts(results[2].data as Product[]);
      } else {
        setWarehouses(results[0].data as Warehouse[]);
        setProducts(results[1].data as Product[]);
      }
    } catch (e) {
      setRefError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefLoading(false);
    }
  }, [companyId, isConsignment]);

  useEffect(() => { fetchRefData(); }, [fetchRefData]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle()
        .then(({ data }) => { if (data?.full_name) setIssuedByName(data.full_name); });
    });
  }, []);

  // ── Form state ────────────────────────────────────────────────────────────
  const [clientId,       setClientId]       = useState('');
  const [sourceWhId,     setSourceWhId]     = useState('');
  const [stdDestWhId,    setStdDestWhId]    = useState('');
  const [issueDate,      setIssueDate]      = useState(today);
  const [notes,          setNotes]          = useState('');
  const [lines,          setLines]          = useState<DocLine[]>(() => {
    if (initialLines?.length) {
      return initialLines.map(l => ({
        product_id:   l.product_id,
        quantity:     String(l.quantity),
        unit_price:   String(l.unit_price),
        line_total:   l.line_total,
      }));
    }
    return [{ ...EMPTY_LINE }];
  });
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [formError,      setFormError]      = useState<string | null>(null);
  const [issuedByName,   setIssuedByName]   = useState('');
  const lineSort = useDocLineSort();

  const handleSortLines = useCallback((key: string) => {
    const k = key as import('@/hooks/useDocLineSort').DocLineSortKey;
    const nextDir = lineSort.sort?.key === k && lineSort.sort.dir === 'asc' ? 'desc' as const : 'asc' as const;
    lineSort.toggle(k);
    setLines(prev => sortDocLines(prev, { key: k, dir: nextDir }, products));
  }, [lineSort, products]);

  // Auto-resolve destination warehouse from client (consignment mode)
  const selectedClient = useMemo(
    () => isConsignment ? (clients.find(c => c.id === clientId) ?? null) : null,
    [clients, clientId, isConsignment],
  );
  // For WHTCO: source = user picks, dest = auto from client's consignment warehouse.
  // For RTVCO: source = auto from client's consignment warehouse, dest = user picks.
  const clientWhId = selectedClient?.consignment_warehouse_id ?? null;
  const destWhId = isConsignment
    ? (isReturn ? (stdDestWhId || null) : clientWhId)
    : (stdDestWhId || null);
  const resolvedSourceWhId = isReturn ? (clientWhId ?? sourceWhId) : sourceWhId;
  const destWhName = useMemo(
    () => (destWhId ? warehouses.find(w => w.id === destWhId)?.name : null) ?? null,
    [destWhId, warehouses],
  );
  const sourceWhName = useMemo(
    () => (resolvedSourceWhId ? warehouses.find(w => w.id === resolvedSourceWhId)?.name : null) ?? null,
    [resolvedSourceWhId, warehouses],
  );

  const productMap = useMemo(
    () => Object.fromEntries(products.map(p => [p.id, p])) as Record<string, Product>,
    [products],
  );

  // ── Line helpers ──────────────────────────────────────────────────────────
  const addLine = () => setLines(ls => [...ls, { ...EMPTY_LINE }]);
  const removeLine = (i: number) => setLines(ls => ls.length <= 1 ? ls : ls.filter((_, j) => j !== i));

  const updateLine = (i: number, field: keyof DocLine, value: string) => {
    setLines(prev => prev.map((l, j) => {
      if (j !== i) return l;
      const next = { ...l, [field]: value };
      if (field === 'product_id') {
        const p = productMap[value];
        if (p) next.unit_price = String(p.price ?? 0);
      }
      next.line_total = calcLine(parseNum(next.quantity), parseNum(next.unit_price));
      return next;
    }));
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const subtotal  = useMemo(() => lines.reduce((s, l) => s + l.line_total, 0), [lines]);
  const vatAmount = useMemo(() => {
    if (!docType.is_vat_liable) return 0;
    return Math.round(subtotal * (vatRate / 100) * 100) / 100;
  }, [subtotal, vatRate, docType.is_vat_liable]);
  const total = subtotal + vatAmount;

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (isConsignment && !clientId) return 'יש לבחור לקוח';
    if (!resolvedSourceWhId) return isReturn
      ? 'ללקוח הנבחר לא הוגדר מחסן קונסגנציה — יש להגדיר בהגדרות לקוח'
      : 'יש לבחור מחסן מקור';
    if (!destWhId) return (isConsignment && !isReturn)
      ? 'ללקוח הנבחר לא הוגדר מחסן קונסגנציה — יש להגדיר בהגדרות לקוח'
      : 'יש לבחור מחסן יעד';
    if (resolvedSourceWhId === destWhId) return 'מחסן מקור ומחסן יעד לא יכולים להיות זהים';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      if (!l.product_id) return `שורה ${i + 1}: יש לבחור מוצר`;
      if (parseNum(l.quantity) <= 0) return `שורה ${i + 1}: כמות חייבת להיות גדולה מאפס`;
    }
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    if (onBeforeFirstSubmit) {
      const proceed = await onBeforeFirstSubmit();
      if (!proceed) return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const payload = {
        company_id:               companyId,
        document_type_id:         docType.id,
        warehouse_id:             null,
        supplier_id:              null,
        client_id:                (isConsignment ? clientId : null) || null,
        issued_by_name:           issuedByName || null,
        source_warehouse_id:      resolvedSourceWhId,
        destination_warehouse_id: destWhId,
        issue_date:               issueDate,
        subtotal,
        vat_amount:               vatAmount,
        total,
        notes:                    notes.trim() || null,
        lines: lines.map(l => ({
          product_id: l.product_id,
          quantity:   parseNum(l.quantity),
          unit_price: parseNum(l.unit_price),
          discount_pct: 0,
          line_total: l.line_total,
        })),
      };

      const { data: rpcDocId, error: rpcErr } = await supabase.rpc(
        'process_inventory_document',
        { payload },
      );
      if (rpcErr) throw rpcErr;

      const generatedNumber = docType.next_number;
      const docRef = `${docType.prefix || ''}${generatedNumber}`;
      showToast(`${docType.name} ${docRef} הופק בהצלחה`, true);

      const effectiveVatRate = docType.is_vat_liable ? vatRate : 0;
      const printData: InventoryPrintData = {
        docTypeName: docType.name,
        docNumber:   docRef,
        date:        issueDate,
        clientName:  selectedClient?.name ?? undefined,
        lines: lines.map(l => ({
          product_id:   l.product_id,
          product_name: productMap[l.product_id]?.name ?? l.product_id,
          quantity:     parseNum(l.quantity),
          unit_price:   parseNum(l.unit_price),
          discount_pct: 0,
          line_total:   l.line_total,
        })),
        subtotal,
        vatAmount,
        grandTotal:  total,
        vatRate:     effectiveVatRate,
        documentId:  typeof rpcDocId === 'string' ? rpcDocId : undefined,
        clientId:    clientId || undefined,
        issuedByName: issuedByName || undefined,
      };
      onSuccess(docType.name, docRef, printData);

    } catch (e) {
      const msg = e instanceof Error ? e.message
        : (typeof e === 'object' && e !== null && 'message' in e)
          ? String((e as { message: unknown }).message)
          : String(e);
      setFormError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error guards ────────────────────────────────────────────────
  if (refLoading) return (
    <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">טוען נתונים…</span>
    </div>
  );

  if (refError) return (
    <div className="flex items-center gap-3 m-6 px-4 py-3 bg-red-50 border border-red-200
                    rounded-xl text-sm text-red-700">
      <AlertCircle className="w-4 h-4 shrink-0" />{refError}
    </div>
  );

  const fmtCurrency = (n: number) =>
    n.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6" dir="rtl">

      {/* Error banner */}
      {formError && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{formError}
        </div>
      )}

      {/* Header fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Client picker — consignment only */}
        {isConsignment && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-gray-400" />
              לקוח קונסגנציה <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={clients.map(c => ({ value: c.id, label: c.name }))}
              value={clientId}
              onChange={setClientId}
              placeholder="בחר לקוח…"
            />
          </div>
        )}

        {/* Source warehouse */}
        {isReturn ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <WarehouseIcon className="w-3.5 h-3.5 text-gray-400" />
              מחסן מקור (קונסגנציה)
            </label>
            <div className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm
                            bg-gray-50 text-gray-600">
              {sourceWhName
                ? sourceWhName
                : clientId
                  ? <span className="text-amber-600">ללקוח לא הוגדר מחסן קונסגנציה</span>
                  : <span className="text-gray-400">ייקבע אוטומטית לפי הלקוח</span>
              }
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <WarehouseIcon className="w-3.5 h-3.5 text-gray-400" />
              מחסן מקור <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={sourceWhId}
                onChange={e => setSourceWhId(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 border border-gray-300
                           rounded-lg text-sm bg-white focus:outline-none focus:ring-2
                           focus:ring-teal-400 focus:border-transparent pr-3 pl-8"
              >
                <option value="">בחר מחסן…</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Destination warehouse */}
        {(isConsignment && !isReturn) ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <ArrowLeftRight className="w-3.5 h-3.5 text-gray-400" />
              מחסן יעד (קונסגנציה)
            </label>
            <div className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm
                            bg-gray-50 text-gray-600">
              {destWhName
                ? destWhName
                : clientId
                  ? <span className="text-amber-600">ללקוח לא הוגדר מחסן קונסגנציה</span>
                  : <span className="text-gray-400">ייקבע אוטומטית לפי הלקוח</span>
              }
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <ArrowLeftRight className="w-3.5 h-3.5 text-gray-400" />
              מחסן יעד <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={stdDestWhId}
                onChange={e => setStdDestWhId(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 border border-gray-300
                           rounded-lg text-sm bg-white focus:outline-none focus:ring-2
                           focus:ring-teal-400 focus:border-transparent pr-3 pl-8"
              >
                <option value="">בחר מחסן יעד…</option>
                {warehouses.filter(w => w.id !== sourceWhId).map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Date */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            תאריך
          </label>
          <input
            type="date"
            value={issueDate}
            onChange={e => setIssueDate(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-400
                       focus:border-transparent"
          />
        </div>
      </div>

      {/* ── Product Lines ──────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Hash className="w-3.5 h-3.5 text-gray-400" />
            שורות מוצרים
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDocPicker(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                         text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100
                         rounded-lg transition-colors border border-orange-200"
            >
              <Download className="w-3.5 h-3.5" />
              ייבא ממסמך
            </button>
            <button
              type="button"
              onClick={addLine}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                         text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> הוסף שורה
            </button>
          </div>
        </div>

        {showDocPicker && (
          <DocumentPickerModal
            companyId={companyId}
            onClose={() => setShowDocPicker(false)}
            onSelect={(imported) => {
              const newLines: DocLine[] = imported.map(l => ({
                product_id:   l.product_id,
                quantity:     String(l.quantity),
                unit_price:   String(l.unit_price),
                line_total:   l.line_total,
              }));
              setLines(prev => {
                const hasContent = prev.some(l => l.product_id !== '');
                return hasContent ? [...prev, ...newLines] : newLines;
              });
              setShowDocPicker(false);
            }}
          />
        )}

        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <SortableTh label="מוצר" sortKey="product_name" activeKey={lineSort.sort?.key ?? ''} dir={lineSort.sort?.dir ?? 'asc'} onSort={handleSortLines} className="w-[40%]" />
                <SortableTh label="כמות" sortKey="quantity" activeKey={lineSort.sort?.key ?? ''} dir={lineSort.sort?.dir ?? 'asc'} onSort={handleSortLines} className="w-[15%]" />
                <SortableTh label="מחיר יחידה" sortKey="unit_price" activeKey={lineSort.sort?.key ?? ''} dir={lineSort.sort?.dir ?? 'asc'} onSort={handleSortLines} className="w-[15%]" />
                <SortableTh label="סה״כ שורה" sortKey="line_total" activeKey={lineSort.sort?.key ?? ''} dir={lineSort.sort?.dir ?? 'asc'} onSort={handleSortLines} className="w-[20%]" />
                <th className="px-3 py-2.5 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, i) => (
                <tr key={i} className="hover:bg-gray-50/60">
                  {/* Product */}
                  <td className="px-3 py-2">
                    <SearchableSelect
                      options={products.map(p => ({
                        value: p.id,
                        label: p.name,
                        secondary: p.sku || undefined,
                      }))}
                      value={line.product_id}
                      onChange={v => updateLine(i, 'product_id', v)}
                      placeholder="בחר מוצר…"
                      compact
                    />
                  </td>
                  {/* Quantity */}
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={line.quantity}
                      onChange={e => updateLine(i, 'quantity', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm
                                 text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                      dir="ltr"
                    />
                  </td>
                  {/* Unit price */}
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={line.unit_price}
                      onChange={e => updateLine(i, 'unit_price', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm
                                 text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                      dir="ltr"
                    />
                  </td>
                  {/* Line total */}
                  <td className="px-3 py-2 text-sm font-medium text-gray-700 text-center" dir="ltr">
                    {fmtCurrency(line.line_total)}
                  </td>
                  {/* Remove */}
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 1}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50
                                 rounded transition-colors disabled:opacity-30"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Totals ─────────────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <div className="w-64 space-y-1.5 text-sm">
          <div className="flex justify-between text-gray-500">
            <span>סכום לפני מע״מ</span>
            <span dir="ltr">{fmtCurrency(subtotal)}</span>
          </div>
          {docType.is_vat_liable && (
            <div className="flex justify-between text-gray-500">
              <span>מע״מ ({vatRate}%)</span>
              <span dir="ltr">{fmtCurrency(vatAmount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1.5">
            <span>סה״כ</span>
            <span dir="ltr">{fmtCurrency(total)}</span>
          </div>
        </div>
      </div>

      {/* ── Notes ──────────────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-gray-400" />
          הערות
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="הערות למסמך (אופציונלי)…"
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                     resize-none focus:outline-none focus:ring-2 focus:ring-teal-400
                     focus:border-transparent"
        />
      </div>

      {/* ── Submit ─────────────────────────────────────────────────────────── */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700
                     disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm
                     font-medium rounded-lg transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />}
          הפק מסמך
        </button>
      </div>
    </div>
  );
}
